import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import logo from '@assets/images/scriptix-blue-logo@2-1.png';
import logoDark from '@assets/images/scriptix-white-400x99.png';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { PATHS } from '../../router/paths.ts';

// Feedback contact for accessibility issues. Change this to a real monitored
// inbox before publishing; EAA requires the channel to route to an owner.
const FEEDBACK_EMAIL = 'accessibility@scriptix.io';

// Last time this statement was reviewed against the panel's actual state.
const LAST_REVIEWED = '2026-09-09';

const AccessibilityStatement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour, theme } = useThemeContext();

  const sectionStyle = {
    backgroundColor: getColour('thirdLayer'),
    borderColor: getColour('border')
  };

  return (
    <main
      className="h-full w-full overflow-y-auto"
      style={{ backgroundColor: getColour('baseLayer') }}
    >
      <div className="w-full min-h-full flex items-start justify-center sm:p-8 p-4">
        <div
          className="w-full max-w-4xl rounded-2xl shadow-md overflow-y-auto"
          style={{ backgroundColor: getColour('secondLayer') }}
        >
          <header
            className="py-8 px-6 text-center"
            style={{
              background:
                theme === 'dark'
                  ? `linear-gradient(to bottom, rgba(30, 58, 138, 0.2), ${getColour(
                      'baseLayer'
                    )})`
                  : 'linear-gradient(to bottom, #dbeafe, #eff6ff)'
            }}
          >
            <img
              src={theme === 'dark' ? logoDark : logo}
              alt="Scriptix"
              className="mx-auto w-44 cursor-pointer mb-6"
              onClick={() => navigate(PATHS().HOME)}
            />
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ color: getColour('normalText') }}
            >
              {t('accessibility-statement')}
            </h1>
            <p
              className="text-sm max-w-2xl mx-auto"
              style={{ color: getColour('inactiveText') }}
            >
              {t('accessibility-statement-intro')}
            </p>
          </header>

          <div className="p-6 space-y-4">
            <section
              className="rounded-xl p-5 border"
              style={sectionStyle}
              aria-labelledby="a11y-conformance"
            >
              <h2
                id="a11y-conformance"
                className="text-lg font-semibold mb-2"
                style={{ color: getColour('normalText') }}
              >
                {t('accessibility-conformance-heading')}
              </h2>
              <p
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-conformance-body')}
              </p>
            </section>

            <section
              className="rounded-xl p-5 border"
              style={sectionStyle}
              aria-labelledby="a11y-scope"
            >
              <h2
                id="a11y-scope"
                className="text-lg font-semibold mb-2"
                style={{ color: getColour('normalText') }}
              >
                {t('accessibility-scope-heading')}
              </h2>
              <p
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-scope-body')}
              </p>
            </section>

            <section
              className="rounded-xl p-5 border"
              style={sectionStyle}
              aria-labelledby="a11y-limitations"
            >
              <h2
                id="a11y-limitations"
                className="text-lg font-semibold mb-2"
                style={{ color: getColour('normalText') }}
              >
                {t('accessibility-limitations-heading')}
              </h2>
              <p
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-limitations-body')}
              </p>
            </section>

            <section
              className="rounded-xl p-5 border"
              style={sectionStyle}
              aria-labelledby="a11y-assessment"
            >
              <h2
                id="a11y-assessment"
                className="text-lg font-semibold mb-2"
                style={{ color: getColour('normalText') }}
              >
                {t('accessibility-assessment-heading')}
              </h2>
              <p
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-assessment-body')}
              </p>
            </section>

            <section
              className="rounded-xl p-5 border"
              style={sectionStyle}
              aria-labelledby="a11y-compatibility"
            >
              <h2
                id="a11y-compatibility"
                className="text-lg font-semibold mb-2"
                style={{ color: getColour('normalText') }}
              >
                {t('accessibility-compatibility-heading')}
              </h2>
              <p
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-compatibility-body')}
              </p>
            </section>

            <section
              className="rounded-xl p-5 border"
              style={sectionStyle}
              aria-labelledby="a11y-feedback"
            >
              <h2
                id="a11y-feedback"
                className="text-lg font-semibold mb-2"
                style={{ color: getColour('normalText') }}
              >
                {t('accessibility-feedback-heading')}
              </h2>
              <p
                className="text-sm mb-3"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-feedback-body')}
              </p>
              <p className="text-sm">
                <a
                  href={`mailto:${FEEDBACK_EMAIL}`}
                  className="font-medium hover:underline"
                  style={{ color: getColour('activeText') }}
                >
                  {FEEDBACK_EMAIL}
                </a>
              </p>
              <p
                className="text-xs mt-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-feedback-sla')}
              </p>
            </section>

            <section
              className="rounded-xl p-5 border"
              style={sectionStyle}
              aria-labelledby="a11y-preparation"
            >
              <h2
                id="a11y-preparation"
                className="text-lg font-semibold mb-2"
                style={{ color: getColour('normalText') }}
              >
                {t('accessibility-preparation-heading')}
              </h2>
              <p
                className="text-sm"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-preparation-body')}
              </p>
              <p
                className="text-xs mt-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('accessibility-last-reviewed', { date: LAST_REVIEWED })}
              </p>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AccessibilityStatement;
